import { Box, Typography, IconButton, Chip } from '@mui/material'
import React from 'react'
import { MdOutlineFileDownload } from "react-icons/md";
import { enterpriseTokens } from 'styles/theme';

const dashboardCopy = {
    "Schedule Overview": {
        title: "Schedule Delivery",
        description: "Compare completion progress and milestone outcomes against plan.",
    },
    "Cost Overview": {
        title: "Cost Position",
        description: "Monitor approved budget, forecast outturn, and delivery efficiency.",
    },
    "Risk Overview": {
        title: "Risk Exposure",
        description: "Review active exposure, resolution progress, and overall risk intensity.",
    },
    "Incident": {
        eyebrow: "Safety events",
        title: "Incident Profile",
        description: "See reported safety events by type and severity.",
    },
    "Incident Status": {
        eyebrow: "Resolution control",
        title: "Incident Resolution",
        description: "Compare open, resolved, and pending safety incidents.",
    },
    "Ageing Analysis - Incident": {
        eyebrow: "Time exposure",
        title: "Incident Ageing",
        description: "Identify unresolved incidents requiring timely corrective action.",
    },
    "Ageing Analysis - NCR": {
        eyebrow: "Quality ageing",
        title: "NCR Ageing",
        description: "Identify older non-conformances requiring escalation.",
    },
    "NCR - Open Vs Closed": {
        eyebrow: "Quality resolution",
        title: "NCR Resolution",
        description: "Compare open and closed quality observations across assets.",
    },
    "HSE Training": {
        eyebrow: "Workforce readiness",
        title: "HSE Training Delivery",
        description: "Compare planned and completed workforce safety training.",
    },
    "EHS Trainings": {
        eyebrow: "Capability coverage",
        title: "EHS Training Coverage",
        description: "Review workforce participation across environment, health, and safety programmes.",
    },
    "Number Of Training": {
        eyebrow: "Plan attainment",
        title: "Training Plan Attainment",
        description: "Measure completed safety training against the annual plan.",
    },
    "Number Of Safety Audit": {
        eyebrow: "Assurance delivery",
        title: "Safety Audit Attainment",
        description: "Measure completed safety audits against the planned programme.",
    },
    "Safety Visits": {
        eyebrow: "Field engagement",
        title: "Safety Visit Coverage",
        description: "Review field safety engagement and oversight activity.",
    },
    "Audits": {
        eyebrow: "Assurance coverage",
        title: "Audit Coverage",
        description: "Track planned and completed assurance activity.",
    },
    "Monitoring & Measurement": {
        eyebrow: "Control monitoring",
        title: "HSE Monitoring Coverage",
        description: "Review completion of scheduled environmental and safety checks.",
    },
    "Environment Reporting": {
        eyebrow: "Environmental control",
        title: "Environmental Performance",
        description: "Monitor environmental observations, reporting, and compliance outcomes.",
    },
    "Procurement Status - Services": {
        eyebrow: "Services pipeline",
        title: "Services Procurement Pipeline",
        description: "Track service packages through each procurement stage.",
    },
    "Procurement Status - Bought Out Items": {
        eyebrow: "Materials pipeline",
        title: "Materials Procurement Pipeline",
        description: "Track bought-out items through each procurement stage.",
    },
    "Work Order by Department": {
        eyebrow: "Commitment mix",
        title: "Department Work Order Mix",
        description: "Compare work-order commitments across project departments.",
    },
    "Ageing Analysis - Services": {
        eyebrow: "Procurement ageing",
        title: "Service RFQ Ageing",
        description: "Identify service enquiries requiring commercial follow-up.",
    },
    "Ageing Analysis - Bought Out Items": {
        eyebrow: "Procurement ageing",
        title: "Material Requisition Ageing",
        description: "Identify older purchase requisitions requiring intervention.",
    },
    "Long Lead items Summary": {
        eyebrow: "Schedule exposure",
        title: "Long-Lead Commitments",
        description: "Monitor critical packages that could constrain project delivery dates.",
    },
    "Floating of RFQ Due this Month": {
        eyebrow: "Near-term actions",
        title: "RFQs Due This Month",
        description: "Review service enquiries scheduled for issue during the current month.",
    },
    " Release of PO Due this Week": {
        eyebrow: "Near-term actions",
        title: "Purchase Orders Due This Week",
        description: "Review purchase orders requiring release during the current week.",
    },
    "Top 10 Vendors by Spend for Services": {
        eyebrow: "Supplier exposure",
        title: "Leading Service Vendors",
        description: "Compare committed service spend across the highest-value vendors.",
    },
    "Top 10 Vendors by Spend for Bought Out Items": {
        eyebrow: "Supplier exposure",
        title: "Leading Material Vendors",
        description: "Compare bought-out item spend across the highest-value vendors.",
    },
};

const TitleBar = ({ title, description, onExport }) => {
    const copy = dashboardCopy[title];
    const resolvedTitle = copy?.title || title;
    const resolvedDescription = copy?.description || description?.trim();

    return (
        <Box sx={{
            width: '100%',
            borderBottom: `1px solid ${enterpriseTokens.lineSoft}`,
            px: 1.5,
            py: 1.25,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxSizing: "border-box",
            backgroundColor: "#FBFCFE",
        }}>
            <Box>
                <Chip
                    label={copy?.eyebrow || "Performance insight"}
                    size="small"
                    sx={{ mb: 0.5, height: 19, borderRadius: "999px", bgcolor: enterpriseTokens.primarySoft, color: enterpriseTokens.primaryDark, fontWeight: 600, fontSize: "9px", textTransform: "uppercase" }}
                />
                <Typography variant="subtitle1" sx={{
                    fontFamily: "Inter, Outfit, sans-serif",
                    fontWeight: 600,
                    fontSize: '12px',
                    lineHeight: '20px',
                    letterSpacing: "0px",
                    mb: 0,
                    color: enterpriseTokens.navy
                }}>
                    {resolvedTitle}
                </Typography>
                <Typography variant="body2" sx={{
                    fontFamily: "Inter, Outfit, sans-serif",
                    fontWeight: 500,
                    fontSize: '11px',
                    lineHeight: '16px',
                    letterSpacing: "0px",
                    color: enterpriseTokens.muted
                }}>
                    {resolvedDescription}
                </Typography>
            </Box>

            {onExport && (
                <IconButton onClick={onExport} sx={{ color: enterpriseTokens.primary, border: `1px solid ${enterpriseTokens.lineSoft}`, borderRadius: '8px' }}>
                    <MdOutlineFileDownload size={24} />
                </IconButton>
            )}

        </Box>
    )
}

export default TitleBar
